import type {
  ChatMessage,
  ClientEvent,
  ComposerHint,
  ConversationSnapshot,
  ConversationSummary,
  UserContent,
} from '@oneapp/shared';
import { isInteractive } from '@oneapp/shared';

/** How the live connection is doing. */
export type Connection = 'connecting' | 'open' | 'reconnecting' | 'offline' | 'closed';

/** Why a message didn't go: shown under it, with "Retry". */
export type SendError = 'offline' | 'busy' | 'rateLimited' | 'server';

/** A question an event answers: the server closes every open question when anything is sent. */
export interface Closing {
  messageId: string;
  selected?: string;
}

/** Something the person sent that the server hasn't confirmed yet. */
export interface Outgoing {
  /** Also the Idempotency-Key, so a retry is handled once. */
  clientId: string;
  event: ClientEvent | { type: 'code'; challengeId: string; from: 'page' | 'box' };
  /** What the person sees until the server's copy of their message arrives. */
  content: UserContent;
  state: 'sending' | 'failed';
  error?: SendError;
  closes: Closing[];
  at: string;
}

export interface ChatState {
  status: 'loading' | 'ready' | 'error' | 'notFound';
  loadError?: 'offline' | 'server';
  conversation: ConversationSummary | null;
  /** Oldest first, by seq. */
  messages: ChatMessage[];
  hasMore: boolean;
  loadingOlder: boolean;
  outgoing: Outgoing[];
  /** Questions answered by events the server accepted (its own update may still be on the way). */
  closed: Record<string, string | true>;
  typing: boolean;
  progress: string | null;
  connection: Connection;
  /** The conversation was deleted, or the person signed out, elsewhere. */
  ended: 'deleted' | 'signed_out' | null;
  notice: 'movedOn' | null;
  /** Message ids from the first load: widgets open by themselves only for messages that arrive live. */
  initialIds: Set<string>;
}

export type ChatAction =
  | { type: 'reset' }
  | { type: 'loadFailed'; reason: 'offline' | 'server' | 'notFound' }
  | { type: 'snapshot'; snapshot: ConversationSnapshot }
  | { type: 'messages'; messages: ChatMessage[] }
  | { type: 'olderLoading' }
  | { type: 'older'; messages: ChatMessage[]; hasMore: boolean }
  | { type: 'olderFailed' }
  | { type: 'typing'; on: boolean }
  | { type: 'progress'; text: string }
  | { type: 'connection'; connection: Connection }
  | { type: 'ended'; reason: 'deleted' | 'signed_out' }
  | { type: 'conversation'; conversation: ConversationSummary }
  | { type: 'send'; outgoing: Outgoing }
  | { type: 'sent'; clientId: string }
  | { type: 'sendFailed'; clientId: string; error: SendError }
  | { type: 'retry'; clientId: string }
  | { type: 'discard'; clientId: string; notice?: 'movedOn' }
  | { type: 'clearNotice' };

export function initialChatState(): ChatState {
  return {
    status: 'loading',
    conversation: null,
    messages: [],
    hasMore: false,
    loadingOlder: false,
    outgoing: [],
    closed: {},
    typing: false,
    progress: null,
    connection: 'connecting',
    ended: null,
    notice: null,
    initialIds: new Set(),
  };
}

/** Adds or updates messages by id, keeping the newest revision of each, in conversation order. */
export function mergeMessages(current: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  if (!incoming.length) return current;
  const byId = new Map(current.map((m) => [m.id, m]));
  let changed = false;
  for (const message of incoming) {
    const known = byId.get(message.id);
    if (known && known.rev >= message.rev) continue;
    byId.set(message.id, message);
    changed = true;
  }
  return changed ? [...byId.values()].sort((a, b) => a.seq - b.seq) : current;
}

/** Outgoing messages whose server copy has arrived are done: their answers now count as settled. */
function settle(state: ChatState, messages: ChatMessage[]): Pick<ChatState, 'outgoing' | 'closed'> {
  const arrived = new Set(messages.map((m) => m.clientRef).filter(Boolean));
  if (!arrived.size) return { outgoing: state.outgoing, closed: state.closed };
  const closed = { ...state.closed };
  const outgoing = state.outgoing.filter((o) => {
    if (!arrived.has(o.clientId)) return true;
    for (const c of o.closes) closed[c.messageId] = c.selected ?? true;
    return false;
  });
  return { outgoing, closed };
}

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'reset':
      return initialChatState();
    case 'loadFailed':
      return action.reason === 'notFound'
        ? { ...state, status: 'notFound' }
        : { ...state, status: 'error', loadError: action.reason };
    case 'snapshot': {
      const { snapshot } = action;
      const messages = mergeMessages(state.status === 'ready' ? state.messages : [], snapshot.messages);
      return {
        ...state,
        ...settle(state, snapshot.messages),
        status: 'ready',
        conversation: snapshot.conversation,
        messages,
        hasMore: state.status === 'ready' ? state.hasMore || snapshot.hasMore : snapshot.hasMore,
        initialIds: state.status === 'ready' ? state.initialIds : new Set(snapshot.messages.map((m) => m.id)),
      };
    }
    case 'messages': {
      const messages = mergeMessages(state.messages, action.messages);
      const botArrived = action.messages.some((m) => m.from === 'bot');
      return {
        ...state,
        ...settle(state, action.messages),
        messages,
        progress: botArrived ? null : state.progress,
        typing: botArrived ? false : state.typing,
      };
    }
    case 'olderLoading':
      return { ...state, loadingOlder: true };
    case 'older':
      return {
        ...state,
        messages: mergeMessages(state.messages, action.messages),
        hasMore: action.hasMore,
        loadingOlder: false,
        initialIds: new Set([...state.initialIds, ...action.messages.map((m) => m.id)]),
      };
    case 'olderFailed':
      return { ...state, loadingOlder: false };
    case 'progress':
      return { ...state, typing: true, progress: action.text };
    case 'typing':
      return { ...state, typing: action.on, progress: action.on ? state.progress : null };
    case 'connection':
      return {
        ...state,
        connection: action.connection,
        progress: action.connection === 'open' ? state.progress : null,
        typing: action.connection === 'open' ? state.typing : false,
      };
    case 'ended':
      return { ...state, ended: action.reason, typing: false, progress: null, connection: 'closed' };
    case 'conversation':
      return state.conversation?.id === action.conversation.id
        ? { ...state, conversation: action.conversation }
        : state;
    case 'send':
      return { ...state, outgoing: [...state.outgoing, action.outgoing], notice: null };
    case 'sent': {
      // Accepted: its answers count even before the server's copy of the message reaches us.
      const sent = state.outgoing.find((o) => o.clientId === action.clientId);
      if (!sent) return state;
      const closed = { ...state.closed };
      for (const c of sent.closes) closed[c.messageId] = c.selected ?? true;
      return { ...state, closed };
    }
    case 'sendFailed':
      return {
        ...state,
        outgoing: state.outgoing.map((o) =>
          o.clientId === action.clientId ? { ...o, state: 'failed', error: action.error } : o,
        ),
      };
    case 'retry':
      return {
        ...state,
        outgoing: state.outgoing.map((o) => {
          if (o.clientId !== action.clientId) return o;
          const { error: _error, ...rest } = o;
          return { ...rest, state: 'sending' };
        }),
      };
    case 'discard':
      return {
        ...state,
        outgoing: state.outgoing.filter((o) => o.clientId !== action.clientId),
        notice: action.notice ?? state.notice,
      };
    case 'clearNotice':
      return { ...state, notice: null };
  }
}

// ---------------------------------------------------------------------------
// What the screen shows, worked out from the state
// ---------------------------------------------------------------------------

/**
 * A message's question status as the person should see it: answered as soon as they've answered it
 * here, without waiting for the server's update.
 */
export function effectiveStatus(
  state: ChatState,
  message: ChatMessage,
): { status?: ChatMessage['status']; selected?: string } {
  if (message.status !== 'active') return { status: message.status, selected: message.selected };
  const settled = state.closed[message.id];
  if (settled !== undefined) return { status: 'answered', selected: settled === true ? undefined : settled };
  for (const o of state.outgoing) {
    if (o.state !== 'sending') continue;
    const closing = o.closes.find((c) => c.messageId === message.id);
    if (closing) return { status: 'answered', selected: closing.selected };
  }
  return { status: 'active' };
}

/** The questions still open, newest last. */
export function openQuestions(state: ChatState): ChatMessage[] {
  return state.messages.filter((m) => m.from === 'bot' && effectiveStatus(state, m).status === 'active');
}

/** How the composer should behave: set by the latest bot message. */
export function composerHint(state: ChatState): ComposerHint | null {
  for (let i = state.messages.length - 1; i >= 0; i--) {
    const message = state.messages[i] as ChatMessage;
    if (message.from === 'bot') return message.composer ?? null;
  }
  return null;
}

/** Typed FILE names the newest usable proposal card visible in this tab. */
export function proposalForFile(messages: ChatMessage[], text: string): string | undefined {
  if (!/^file[.!\s]*$/i.test(text.trim())) return undefined;
  // The newest question on screen, even if a reply like "ok" in between answered it (the server decides whether
  // its proposal is still live). If that question isn't a proposal card, FILE goes without an id.
  const card = [...messages].reverse().find((message) => message.from === 'bot' && isInteractive(message.content));
  if (!card || card.status === 'expired') return undefined;
  return 'proposalId' in card.content ? card.content.proposalId : undefined;
}
